import { useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { LoadingState } from "@/components/base/LoadingState";
import { Modal } from "@/components/base/Modal";
import { ConfirmDialog } from "@/components/base/ConfirmDialog";
import { SettingsNav } from "@/pages/settings/components/SettingsNav";
import { AdminOnly } from "@/components/feature/AdminOnly";
import { initials, fmtDate } from "@/lib/format";
import { friendlyError } from "@/lib/errors";
import { sendInviteEmail } from "@/lib/invitations";

interface MembershipRow { id: string; user_id: string; role: string; joined_at: string; }
interface ProfileRow { id: string; full_name: string | null; email: string | null; }
interface RoleRow { key: string; name: string; }
interface InvitationRow { id: string; email: string; role: string; status: string; created_at: string; expires_at: string | null; invitation_kind?: string; new_org_name?: string | null; }

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

export default function UsersSettings() {
  const { org, user, role, refresh } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [savingId, setSavingId] = useState<string | null>(null);
  const [roleError, setRoleError] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [businessInviteOpen, setBusinessInviteOpen] = useState(false);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<MembershipRow | null>(null);
  const [notice, setNotice] = useState("");
  const [inviteForm, setInviteForm] = useState({ email: "", role: "readonly" });
  const [businessForm, setBusinessForm] = useState({ email: "", orgName: "" });

  const isAdmin = role === "owner" || role === "admin";
  const isOwner = role === "owner";

  const { data, isLoading } = useQuery({
    queryKey: ["settings-users", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const [memberships, roles, invitations] = await Promise.all([
        supabase.from("memberships").select("id, user_id, role, joined_at").eq("org_id", org!.id).order("joined_at"),
        supabase.from("roles").select("key, name").order("name"),
        supabase.from("invitations").select("id, email, role, status, created_at, expires_at, invitation_kind, new_org_name").eq("org_id", org!.id).order("created_at", { ascending: false }),
      ]);
      const userIds = (memberships.data ?? []).map((m) => (m as MembershipRow).user_id);
      const profilesRes = userIds.length
        ? await supabase.from("profiles").select("id, full_name, email").in("id", userIds)
        : { data: [] };
      return {
        memberships: (memberships.data ?? []) as MembershipRow[],
        profiles: (profilesRes.data ?? []) as ProfileRow[],
        roles: (roles.data ?? []) as RoleRow[],
        invitations: (invitations.data ?? []) as InvitationRow[],
      };
    },
  });

  const profileById = useMemo(() => {
    const map: Record<string, ProfileRow> = {};
    for (const p of data?.profiles ?? []) map[p.id] = p;
    return map;
  }, [data]);

  const memberName = (m: MembershipRow | null) => {
    if (!m) return "";
    const p = profileById[m.user_id];
    return p?.full_name || p?.email || m.user_id.slice(0, 8);
  };

  // Owners can grant/demote the owner role; admins cannot. Don't offer an
  // option the backend will refuse.
  const selectableRoles = useMemo(
    () => (data?.roles ?? []).filter((r) => isOwner || r.key !== "owner"),
    [data, isOwner],
  );

  // invite_member does not accept the "owner" role (owners are promoted via
  // set_member_role), so never offer it in the invite dropdown — otherwise an
  // owner would see an option the backend always rejects.
  const invitableRoles = useMemo(
    () => (data?.roles ?? []).filter((r) => r.key !== "owner"),
    [data],
  );

  const ownerCount = (data?.memberships ?? []).filter((m) => m.role === "owner").length;
  const pendingInvites = (data?.invitations ?? []).filter((i) => i.status === "pending");
  const acceptedCount = (data?.invitations ?? []).filter((i) => i.status === "accepted").length;

  const changeRole = async (membershipId: string, newRole: string) => {
    setSavingId(membershipId);
    setRoleError("");
    const { error } = await supabase.rpc("set_member_role", {
      p_org_id: org!.id,
      p_membership_id: membershipId,
      p_role: newRole,
    });
    setSavingId(null);
    if (error) {
      setRoleError(friendlyError(error.message));
    }
    queryClient.invalidateQueries({ queryKey: ["settings-users", org?.id] });
  };

  const handleRemove = async () => {
    if (!confirmTarget) return;
    const wasSelf = confirmTarget.user_id === user?.id;
    setRemovingId(confirmTarget.id);
    const { error } = await supabase.rpc("remove_member", {
      p_org_id: org!.id,
      p_membership_id: confirmTarget.id,
    });
    setRemovingId(null);
    setConfirmTarget(null);
    if (error) {
      setNotice(friendlyError(error.message));
      return;
    }
    if (wasSelf) {
      await refresh();
      navigate("/");
    } else {
      queryClient.invalidateQueries({ queryKey: ["settings-users", org?.id] });
    }
  };

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault();
    setInviteError("");
    setNotice("");
    setInviteSaving(true);
    const { data: inviteData, error } = await supabase.rpc("invite_member", {
      p_org_id: org!.id,
      p_email: inviteForm.email,
      p_role: inviteForm.role,
    });
    setInviteSaving(false);
    if (error) {
      setInviteError(friendlyError(error.message));
      return;
    }
    const token = (inviteData as { token?: string } | null)?.token;
    const emailResult = await sendInviteEmail(inviteForm.email, org!.name, inviteForm.role, token);
    setInviteOpen(false);
    setInviteForm({ email: "", role: "readonly" });
    if (emailResult.sent) {
      setNotice(`Invitation sent to ${inviteForm.email}.`);
    } else if (emailResult.reason === "email_not_configured") {
      setNotice("Invitation created, but no email was sent because email delivery isn't configured yet.");
    } else {
      setNotice("Invitation created. The invitation email couldn't be sent.");
    }
    queryClient.invalidateQueries({ queryKey: ["settings-users", org?.id] });
  };

  const handleBusinessInvite = async (e: FormEvent) => {
    e.preventDefault();
    setInviteError("");
    setNotice("");
    setInviteSaving(true);
    const { data: inviteData, error } = await supabase.rpc("invite_independent_business", {
      p_issuer_org_id: org!.id,
      p_email: businessForm.email,
      p_new_org_name: businessForm.orgName,
    });
    setInviteSaving(false);
    if (error) {
      setInviteError(friendlyError(error.message));
      return;
    }
    const token = (inviteData as { token?: string } | null)?.token;
    const emailResult = await sendInviteEmail(businessForm.email, businessForm.orgName, "owner", token);
    setBusinessInviteOpen(false);
    setBusinessForm({ email: "", orgName: "" });
    if (emailResult.sent) {
      setNotice(`Start-your-own-business invitation sent to ${businessForm.email}.`);
    } else if (emailResult.reason === "email_not_configured") {
      setNotice("Invitation created, but no email was sent because email delivery isn't configured yet.");
    } else {
      setNotice("Invitation created. The invitation email couldn't be sent.");
    }
    queryClient.invalidateQueries({ queryKey: ["settings-users", org?.id] });
  };

  const handleResend = async (inv: InvitationRow) => {
    setNotice("");
    setResendingId(inv.id);
    const { data: resendData, error } = await supabase.rpc("resend_invitation", {
      p_invite_id: inv.id,
    });
    if (error) {
      setResendingId(null);
      setNotice(friendlyError(error.message));
      return;
    }
    const token = (resendData as { token?: string } | null)?.token;
    const emailResult = await sendInviteEmail(
      inv.email,
      inv.invitation_kind === "create_organisation" ? (inv.new_org_name || inv.email) : org!.name,
      inv.role,
      token,
    );
    setResendingId(null);
    if (emailResult.sent) setNotice(`Invitation re-sent to ${inv.email}.`);
    else if (emailResult.reason === "email_not_configured") setNotice("Invitation refreshed, but no email was sent because email delivery isn't configured yet.");
    else setNotice("Invitation refreshed. The invitation email couldn't be sent.");
    queryClient.invalidateQueries({ queryKey: ["settings-users", org?.id] });
  };

  const handleCancel = async (invitationId: string) => {
    setNotice("");
    setCancellingId(invitationId);
    const { error } = await supabase.rpc("cancel_invitation", { p_invite_id: invitationId });
    setCancellingId(null);
    if (error) {
      setNotice(friendlyError(error.message));
    } else {
      setNotice("Invitation cancelled.");
    }
    queryClient.invalidateQueries({ queryKey: ["settings-users", org?.id] });
  };

  return (
    <AdminOnly title="Team Members">
    <div>
      <PageHeader title="Team Members" subtitle="Manage people and their access">
        {isAdmin && (
          <div className="flex items-center gap-2">
            {isOwner && (
              <button
                onClick={() => { setInviteError(""); setBusinessInviteOpen(true); }}
                className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-building-line" /> Start your own business
              </button>
            )}
            <button
              onClick={() => { setInviteError(""); setInviteOpen(true); }}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-user-add-line" /> Invite member
            </button>
          </div>
        )}
      </PageHeader>

      {notice && (
        <div className="mb-4 px-4 py-2.5 rounded-lg text-sm bg-slate-50 border border-slate-200 text-slate-600">{notice}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {isLoading ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8"><LoadingState /></div>
          ) : (
            <>
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <h3 className="text-sm font-semibold text-slate-800">Current members</h3>
                </div>
                {roleError && (
                  <div className="px-4 py-2.5 bg-red-50 border-b border-red-100 text-sm text-red-700">{roleError}</div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50/70 border-b border-slate-100">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Member</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Joined</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data?.memberships ?? []).map((m) => {
                        const profile = profileById[m.user_id];
                        const isSelf = m.user_id === user?.id;
                        const name = profile?.full_name || profile?.email || m.user_id.slice(0, 8);
                        const lastOwner = isSelf && role === "owner" && ownerCount <= 1;
                        return (
                          <tr key={m.id} className="border-b border-slate-50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-xs font-semibold">
                                  {initials(name)}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-slate-800">{name}{isSelf ? " (you)" : ""}</p>
                                  {profile?.email && <p className="text-xs text-slate-400">{profile.email}</p>}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <select
                                value={m.role}
                                disabled={isSelf || !isAdmin || savingId === m.id}
                                onChange={(e) => changeRole(m.id, e.target.value)}
                                className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm capitalize disabled:opacity-50"
                              >
                                {selectableRoles.map((r) => (
                                  <option key={r.key} value={r.key}>{r.name}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500">
                              {new Date(m.joined_at).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-1">
                                {isSelf ? (
                                  <button
                                    onClick={() => setConfirmTarget(m)}
                                    disabled={removingId === m.id || lastOwner}
                                    title={lastOwner ? "You are the last owner and cannot leave" : "Leave organization"}
                                    className="px-3 py-1.5 text-xs font-medium rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-40 whitespace-nowrap"
                                  >
                                    Leave
                                  </button>
                                ) : (
                                  isAdmin && (
                                    <button
                                      onClick={() => setConfirmTarget(m)}
                                      disabled={removingId === m.id}
                                      className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
                                      title="Remove member"
                                    >
                                      <i className="ri-user-unfollow-line" />
                                    </button>
                                  )
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-800">Pending invitations</h3>
                  {acceptedCount > 0 && (
                    <span className="text-xs text-slate-400">{acceptedCount} accepted</span>
                  )}
                </div>
                {pendingInvites.length === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <p className="text-sm text-slate-500">No pending invitations.</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Invite someone by email — they'll join automatically when they create an account.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-50">
                    {pendingInvites.map((inv) => {
                      const isExpired = inv.expires_at ? new Date(inv.expires_at).getTime() < Date.now() : false;
                      return (
                        <div key={inv.id} className="px-4 py-3 flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{inv.email}</p>
                            <p className="text-xs text-slate-400 mt-0.5 capitalize">
                              {inv.invitation_kind === "create_organisation"
                                ? `New business${inv.new_org_name ? ` · ${inv.new_org_name}` : ""}`
                                : inv.role}
                              {" · "}invited {fmtDate(inv.created_at)}
                              {inv.expires_at && <span className="ml-1">· expires {fmtDate(inv.expires_at)}</span>}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${isExpired ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                              {isExpired ? "Expired" : "Awaiting signup"}
                            </span>
                            {isAdmin && (
                              <button
                                onClick={() => handleResend(inv)}
                                disabled={resendingId === inv.id}
                                className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-50"
                                title="Resend invitation"
                              >
                                <i className="ri-mail-send-line" />
                              </button>
                            )}
                            {isAdmin && (
                              <button
                                onClick={() => handleCancel(inv.id)}
                                disabled={cancellingId === inv.id}
                                className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
                                title="Cancel invitation"
                              >
                                <i className="ri-close-line" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <SettingsNav />
      </div>

      <Modal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Invite team member"
        footer={
          <>
            <button
              onClick={() => setInviteOpen(false)}
              className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleInvite}
              disabled={inviteSaving}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {inviteSaving ? "Inviting..." : "Send invite"}
            </button>
          </>
        }
      >
        <form onSubmit={handleInvite} className="space-y-4">
          {inviteError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{inviteError}</div>}
          <div>
            <label className={labelCls}>Email *</label>
            <input
              type="email"
              required
              value={inviteForm.email}
              onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
              className={inputCls}
              placeholder="teammate@company.com"
            />
          </div>
          <div>
            <label className={labelCls}>Role</label>
            <select
              value={inviteForm.role}
              onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value }))}
              className={inputCls}
            >
              {invitableRoles.map((r) => (
                <option key={r.key} value={r.key}>{r.name}</option>
              ))}
            </select>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">
            When they create an account with this email, they'll automatically join your organization with this role.
          </p>
        </form>
      </Modal>

      <Modal
        open={businessInviteOpen}
        onClose={() => setBusinessInviteOpen(false)}
        title="Start your own business"
        footer={
          <>
            <button
              onClick={() => setBusinessInviteOpen(false)}
              className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleBusinessInvite}
              disabled={inviteSaving}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {inviteSaving ? "Inviting..." : "Send invitation"}
            </button>
          </>
        }
      >
        <form onSubmit={handleBusinessInvite} className="space-y-4">
          {inviteError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{inviteError}</div>}
          <p className="text-sm text-slate-600 leading-relaxed">
            This creates a separate Tradivo organisation. The invitee becomes owner of that business. They will not join this organisation, and this organisation will not receive operational access to theirs.
          </p>
          <div>
            <label className={labelCls}>Invitee email *</label>
            <input
              type="email"
              required
              value={businessForm.email}
              onChange={(e) => setBusinessForm((f) => ({ ...f, email: e.target.value }))}
              className={inputCls}
              placeholder="owner@their-business.com"
            />
          </div>
          <div>
            <label className={labelCls}>New organisation name *</label>
            <input
              type="text"
              required
              value={businessForm.orgName}
              onChange={(e) => setBusinessForm((f) => ({ ...f, orgName: e.target.value }))}
              className={inputCls}
              placeholder="Their business name"
            />
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!confirmTarget}
        title={confirmTarget?.user_id === user?.id ? "Leave organization" : "Remove member"}
        message={
          confirmTarget?.user_id === user?.id
            ? "Leave this organization? You'll lose access to its data immediately and can only rejoin if you're re-invited."
            : `Remove ${memberName(confirmTarget)} from the organization? They'll lose access immediately.`
        }
        confirmLabel={confirmTarget?.user_id === user?.id ? "Leave" : "Remove"}
        loading={removingId === confirmTarget?.id}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={handleRemove}
      />
    </div>
    </AdminOnly>
  );
}